import { useEffect, useState } from 'react';
import { SingleValue } from 'react-select';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';

import { IStore } from '../../../../services/api/troubleshooting-service';
import CurrentPortalStore from '../../../../stores/current-portal-store';
import { IOption, SelectDropdown } from '../../../../components/inputs/select-dropdown/select-dropdown';
import { FONT_COLOR, FONT_VARIANT } from '../../../../components/fonts/font-props';
import { Input } from '../../../../components/inputs/input/input';
import { Button } from '../../../../components/inputs/button/button';
import FontBody2 from '../../../../components/fonts/font-body2/font-body2';
import LinearLoader from '../../../../components/loaders/linear-loader/linear-loader';

import TroubleshootingStore from '../../troubleshooting-page-store';

import RefundCalculatorDisplay from './refund-calculator-display/refund-calculator-display';
import { IRefundCalculatorResponseDto } from './refund-calculator-types';
import './refund-calculator-page.scss';

const RefundCalculatorPage: React.FC = observer(() => {
    const [chosenStore, setChosenStore] = useState<IOption | null>(null);
    const [rmaId, setRmaId] = useState<string>('');
    const [shopifyResponse, setShopifyResponse] = useState<IRefundCalculatorResponseDto | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        TroubleshootingStore.fetchStores();
        CurrentPortalStore.load();
    }, []);

    const portalId = CurrentPortalStore.currentPortal.id;
    const portalName = CurrentPortalStore.currentPortal.name;
    const portalDisplayName = CurrentPortalStore.currentPortal.displayName;
    const canPerformFetch = chosenStore?.value && rmaId.trim().length > 0;

    useEffect(() => {
        if (chosenStore?.value !== portalId && portalId && portalName) {
            setChosenStore({
                value: portalId,
                label: portalName + (portalDisplayName ? ' (' + portalDisplayName + ')' : '')
            });
        }
    }, [portalId, portalName, portalDisplayName, chosenStore?.value]);

    /** patch to prevent react warning */
    const __shopifyResponse = TroubleshootingStore.shopifyResponse;
    useEffect(() => {
        setShopifyResponse(toJS(TroubleshootingStore.shopifyResponse));
        setIsLoading(false);
    }, [__shopifyResponse]);

    const generateSelectValues = (): IOption[] | [] => {
        return TroubleshootingStore.stores?.map((el: IStore): IOption => ({
            value: el.id,
            label: el.name + (el.display_name ? ' (' + el.display_name + ')' : '')
        })) || [];
    };

    const performFetch = async () => {
        if (chosenStore?.value && rmaId) {
            try {
                setIsLoading(true);
                await TroubleshootingStore.fetchRefundCalculationResults(chosenStore.value, rmaId.replace('RMA', ''));
            } finally {
                setIsLoading(false);
            }
        }
    };

    const refundCalculationReady = Object.keys(shopifyResponse || {}).length > 0;

    return (
        <div className="refund-calculator-page">
            <div className="card">
                <h4>Request Information</h4>
                <div className="controls">
                    <p className="head5">Store</p>
                    <SelectDropdown
                        required
                        placeholder="Type to find and select store"
                        value={chosenStore || undefined}
                        options={generateSelectValues()}
                        onChange={(val: SingleValue<IOption>) => setChosenStore(val)}
                    />
                </div>
                <div className="controls">
                    <p className="head5">RMA ID</p>
                    <Input
                        required
                        value={rmaId}
                        onChange={(val: any) => setRmaId(val)}
                        onSubmit={performFetch}
                    />
                </div>
                <Button
                    text="Fetch"
                    disabled={!canPerformFetch}
                    onClick={performFetch}
                />
            </div>

            {isLoading ? (
                <div className="card">
                    <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                        Loading refund calculation results for RMA{rmaId.replace('RMA', '')}. Please wait...
                        <LinearLoader />
                    </FontBody2>
                </div>
            ) : null}

            {(!isLoading && refundCalculationReady) ? (
                <div className="card results-card">
                    <RefundCalculatorDisplay data={shopifyResponse as IRefundCalculatorResponseDto} />
                </div>
            ) : null}
        </div>
    );
});

export default RefundCalculatorPage;
